import { useEffect, useState } from 'react'
import useSWR from 'swr'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { fetcher } from '@/lib/fetcher'

type MasterPanelProps = {
  enabled?: boolean
}

export function MasterHitlPanel({ enabled = true }: MasterPanelProps) {
  const [available, setAvailable] = useState(true)

  useEffect(() => {
    setAvailable(Boolean(enabled))
  }, [enabled])

  const shouldFetch = enabled && available
  const { data, error, isLoading, mutate } = useSWR(shouldFetch ? '/master/hitl' : null, fetcher, {
    refreshInterval: shouldFetch ? 5000 : 0,
    revalidateOnFocus: shouldFetch,
    onError: (err) => {
      if ((err as { status?: number } | undefined)?.status === 404) {
        setAvailable(false)
      }
    },
  })

  const items = shouldFetch ? data?.items ?? [] : []
  const isUnavailable = enabled && !available

  return (
    <Card className="border border-border/70 bg-background/70">
      <CardHeader className="flex items-center justify-between gap-2">
        <CardTitle className="text-lg">Master HITL inbox</CardTitle>
        <Badge variant="outline" className="text-xs">
          {items.length}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {error && !isUnavailable && <p className="text-xs text-rose-300">Unable to load HITL items</p>}
        {isUnavailable ? (
          <p className="text-xs text-muted-foreground">Master insights are unavailable in this environment.</p>
        ) : isLoading && !items.length ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-xs text-muted-foreground">No HITL items across agents.</p>
        ) : (
          <ul className="space-y-2">
            {items.map((item: any) => (
              <li key={item.id} className="rounded-xl border border-border/80 bg-background/40 p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-foreground">{item.agent ?? 'agent'}</span>
                  <Badge variant="secondary" className="text-xs uppercase">
                    {new Date(item.timestamp ?? Date.now()).toLocaleTimeString()}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">{item.summary ?? item.type}</p>
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          className="text-xs uppercase tracking-wide text-primary-foreground dark:text-amber-200 hover:underline disabled:opacity-50"
          onClick={() => mutate()}
          disabled={!available}
        >
          Refresh
        </button>
      </CardContent>
    </Card>
  )
}
